import { useState } from "react";
import { useLocation } from "wouter";
import { loginBoardSession } from "@workspace/api-client-react";
import { LogoMark } from "@/components/Logo";
import QuoteCarousel from "@/components/QuoteCarousel";
import { Loader2, ArrowLeft, KeyRound } from "lucide-react";

export default function BoardGateway() {
  const [key, setKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, navigate] = useLocation();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const result = await loginBoardSession(key.trim());
      if (result.boardRole === "secretary") {
        navigate("/admin/secretary");
      } else if (result.boardRole === "treasurer") {
        navigate("/admin/treasurer");
      } else {
        navigate("/admin/secretary");
      }
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string };
      const msg =
        apiErr.status === 403
          ? "Invalid gateway key"
          : (apiErr.message ?? "Something went wrong");
      setError(msg);
      setShake(true);
      setKey("");
      setTimeout(() => setShake(false), 500);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "hsl(var(--background))",
      }}
    >
      <nav style={{ borderBottom: "1px solid #334155", padding: "0 20px", height: "52px", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#0f172a" }}>
        <button
          onClick={() => navigate("/")}
          style={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            fontSize: "13px", color: "rgba(203,213,225,0.85)",
            background: "none", border: "none", cursor: "pointer", padding: 0,
            fontFamily: "inherit", transition: "color 0.15s",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "#ffffff"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = "rgba(203,213,225,0.85)"; }}
        >
          <ArrowLeft style={{ width: "14px", height: "14px" }} />
          Back to Home
        </button>
        <span style={{
          display: "inline-flex", alignItems: "center",
          padding: "2px 8px", borderRadius: "999px",
          fontSize: "10px", fontWeight: 700, letterSpacing: "0.08em",
          background: "#dc2626", color: "#ffffff",
        }}>
          ADMIN
        </span>
      </nav>

      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
        <div style={{ width: "100%", maxWidth: "340px" }}>
          <div style={{ textAlign: "center", marginBottom: "32px" }}>
            <div style={{ display: "flex", justifyContent: "center", marginBottom: "12px" }}>
              <div style={{ position: "relative" }}>
                <LogoMark size={36} />
                <div style={{
                  position: "absolute", bottom: "-4px", right: "-8px",
                  background: "hsl(var(--background))", borderRadius: "50%", padding: "2px",
                }}>
                  <KeyRound style={{ width: "14px", height: "14px", color: "hsl(var(--muted-foreground))" }} />
                </div>
              </div>
            </div>
            <p style={{ fontSize: "13px", fontWeight: 600, color: "hsl(var(--foreground))", marginBottom: "4px" }}>
              Board Officer Portal
            </p>
            <p style={{ fontSize: "12px", color: "hsl(var(--muted-foreground))" }}>
              Enter your gateway key to access your dashboard
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ animation: shake ? "shake 0.4s ease" : "none" }}>
              <input
                type="password"
                value={key}
                onChange={(e) => {
                  setKey(e.target.value);
                  setError(null);
                }}
                placeholder="Enter gateway key"
                autoFocus
                autoComplete="off"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  fontSize: "14px",
                  border: `1px solid ${error ? "hsl(var(--destructive))" : "hsl(var(--border))"}`,
                  borderRadius: "8px",
                  background: "hsl(var(--background))",
                  color: "hsl(var(--foreground))",
                  outline: "none",
                  boxSizing: "border-box",
                  marginBottom: "8px",
                  transition: "border-color 0.15s",
                  opacity: loading ? 0.6 : 1,
                  fontFamily: "monospace",
                  letterSpacing: "0.05em",
                }}
              />
              {error && (
                <p style={{ fontSize: "12px", color: "hsl(var(--destructive))", marginBottom: "8px", textAlign: "center" }}>
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={!key.trim() || loading}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "14px",
                  fontWeight: 500,
                  background: "hsl(var(--primary))",
                  color: "hsl(var(--primary-foreground))",
                  border: "none",
                  borderRadius: "8px",
                  cursor: key.trim() && !loading ? "pointer" : "not-allowed",
                  opacity: key.trim() && !loading ? 1 : 0.5,
                  transition: "opacity 0.15s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {loading && <Loader2 style={{ width: "14px", height: "14px", animation: "spin 1s linear infinite" }} />}
                {loading ? "Verifying..." : "Access Portal"}
              </button>
            </div>
          </form>

          <p style={{ fontSize: "11px", color: "hsl(var(--muted-foreground))", textAlign: "center", marginTop: "20px" }}>
            Gateway keys are issued by the organization administrator
          </p>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      <QuoteCarousel />
    </div>
  );
}
